import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { SubtleMonochromeLink } from '@liveauctioneers/hammer-ui-core/link';
import { BodySecondary, Caption, LabelStrong } from '@liveauctioneers/hammer-ui-core/text';
import { memo } from 'react';
import styled, { css } from 'styled-components';

import { FormattedCurrency } from '@/components/Formatted/FormattedCurrency';
import { useAppSelector } from '@/redux/hooks';
import { getLiveItemDataBidCount } from '@/redux/modules/console';
import {
    getItemSummaryBidCount,
    getItemSummaryCatalogId,
    getItemSummaryCurrency,
    getItemSummaryFirstImageUrl,
    getItemSummaryHighBidEstimate,
    getItemSummaryIsPassed,
    getItemSummaryIsSold,
    getItemSummaryLeadingBid,
    getItemSummaryLotNumber,
    getItemSummaryLowBidEstimate,
    getItemSummarySalePrice,
    getItemSummaryStartPrice,
    getItemSummaryTitle,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { useTrackListItemClickedAnalytics } from '@/utils/amplitude/analytics/bidding/trackListItemClicked.analytics';
import { addImageParams } from '@/utils/urls';

import LotImageContainer from '../LotImageContainer/LotImageContainer';

type Props = {
    index: number;
    isLiveItemVisible: boolean;
    itemId: number;
    liveItemId: number;
    liveItemIndex: number;
    lotsUntilUpcoming: number;
};

const ListItem = ({ index, isLiveItemVisible, itemId, liveItemId, liveItemIndex, lotsUntilUpcoming }: Props) => {
    const bidCount = useAppSelector((state) => getItemSummaryBidCount(state, itemId));
    const catalogId = useAppSelector((state) => getItemSummaryCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemSummaryCurrency(state, itemId));
    const highBidEstimate = useAppSelector((state) => getItemSummaryHighBidEstimate(state, itemId));
    const isPassed = useAppSelector((state) => getItemSummaryIsPassed(state, itemId));
    const isSold = useAppSelector((state) => getItemSummaryIsSold(state, itemId));
    const leadingBid = useAppSelector((state) => getItemSummaryLeadingBid(state, itemId));
    const lotNumber = useAppSelector((state) => getItemSummaryLotNumber(state, itemId));
    const lowBidEstimate = useAppSelector((state) => getItemSummaryLowBidEstimate(state, itemId));
    const salePrice = useAppSelector((state) => getItemSummarySalePrice(state, itemId));
    const startPrice = useAppSelector((state) => getItemSummaryStartPrice(state, itemId));
    const title = useAppSelector((state) => getItemSummaryTitle(state, itemId));

    const liveItemBidCount = useAppSelector((state) => getLiveItemDataBidCount(state, itemId));
    const trackListItemClicked = useTrackListItemClickedAnalytics(itemId, catalogId);

    const url = useAppSelector((state) => getItemSummaryFirstImageUrl(state, itemId));
    const imageUrl = addImageParams({ height: 74, url, width: 74 });
    const isLiveItem = liveItemId === itemId;

    // Append spacing if "go to live item" overlay is visible and user is at top of list, prevents overlay from cutting off first item
    const showHeaderSpacer = index === 0 && !isLiveItemVisible;

    // Make list item a disabled color to indicate the item is unavailable to purchase
    const itemUnavailable = isSold || isPassed;
    const itemBidCount = isLiveItem ? liveItemBidCount : bidCount;

    return (
        <StyledListItem
            $isLive={isLiveItem}
            data-testid={`auctionLotScrollerItem-${index}`}
            key={liveItemIndex}
        >
            {showHeaderSpacer && <HeaderSpacer />}
            <MainContainer>
                <LotImageContainer
                    catalogId={catalogId}
                    imageUrl={imageUrl}
                    itemId={itemId}
                    itemUnavailable={itemUnavailable}
                />
                <ItemDetailsContainer $itemUnavailable={itemUnavailable}>
                    <LiveNowText
                        $isLive={isLiveItem}
                        color="critical"
                    >
                        <FormattedMessage id="bidding_console.console.lotTracker.liveNow" />
                    </LiveNowText>
                    {lotsUntilUpcoming && (
                        <NextFavoriteText color="critical">
                            <FormattedMessage
                                id="bidding_console.console.status.nextFavorite.value"
                                values={{ total: lotsUntilUpcoming[itemId] }}
                            />
                        </NextFavoriteText>
                    )}
                    <SubtleMonochromeLink
                        className="mb-2xs line-clamp-2 capitalize"
                        href={`/item/${itemId}`}
                        onClick={trackListItemClicked}
                        target="_blank"
                    >
                        {`${lotNumber}: ${title}`}
                    </SubtleMonochromeLink>
                    {!isPassed && (
                        <StyledSecondaryEstimate
                            $isLive={isLiveItem}
                            color="secondary"
                        >
                            <FormattedMessage
                                id="bidding_console.console.description.estimate_range"
                                values={{
                                    high: (
                                        <FormattedCurrency
                                            convert
                                            key="list-item-estimate-high"
                                            noFraction
                                            sourceCurrency={currency}
                                            value={highBidEstimate}
                                        />
                                    ),
                                    low: (
                                        <FormattedCurrency
                                            convert
                                            key="list-item-estimate-low"
                                            noFraction
                                            sourceCurrency={currency}
                                            value={lowBidEstimate}
                                        />
                                    ),
                                }}
                            />
                        </StyledSecondaryEstimate>
                    )}
                    {!isPassed ? (
                        <PriceAndBidCountContainer>
                            <LabelStrong>
                                {isSold && (
                                    <>
                                        <FormattedMessage id="bidding_console.console.lotTracker.soldFor" />
                                        &nbsp;
                                    </>
                                )}
                                <FormattedCurrency
                                    convert
                                    noFraction
                                    sourceCurrency={currency}
                                    value={
                                        isLiveItem ? leadingBid || startPrice : leadingBid || salePrice || startPrice
                                    }
                                />
                            </LabelStrong>
                            &nbsp;
                            <StyledBidCount
                                $isSold={isSold}
                                color="secondary"
                            >
                                ({itemBidCount}&nbsp;
                                {itemBidCount === 1 ? (
                                    <FormattedMessage id="bidding_console.console.lotTracker.bid" />
                                ) : (
                                    <FormattedMessage id="bidding_console.console.lotTracker.bids" />
                                )}
                                )
                            </StyledBidCount>
                        </PriceAndBidCountContainer>
                    ) : (
                        <LabelStrong>
                            <FormattedMessage id="bidding_console.console.lotTracker.passed" />
                        </LabelStrong>
                    )}
                </ItemDetailsContainer>
            </MainContainer>
            {/* {(bidderHasBid || bidderHasHighBid) &&
                !isPassed &&
                !isSold &&
                itemId !== itemId && <IncreaseBidButton itemId={itemId} />} */}
        </StyledListItem>
    );
};

export default memo<Props>(ListItem);

const StyledListItem = styled.div<{ $isLive: boolean }>`
    min-height: 120px;
    padding: 16px 24px;
    display: flex;
    flex-direction: column;
    border-left: 4px solid ${({ theme }) => theme.colors.white};
    border-bottom: 1px solid ${({ theme }) => theme.colors.grey400};
    background: ${({ theme }) => theme.colors.white};
    ${({ $isLive, theme }) =>
        $isLive &&
        css`
            background: ${theme.colors.beige100};
            border-left: 4px solid ${theme.colors.red100};
        `}

    &:hover .heart-icon {
        display: grid;
    }
`;

const MainContainer = styled.div`
    display: flex;
`;

const ItemDetailsContainer = styled.div<{ $itemUnavailable: boolean }>`
    display: flex;
    flex-direction: column;
    ${({ $itemUnavailable }) =>
        $itemUnavailable &&
        css`
            opacity: 0.5;
        `}
`;

const PriceAndBidCountContainer = styled.span`
    display: flex;
    align-items: center;
`;

const StyledSecondaryEstimate = styled(BodySecondary)<{ $isLive: boolean }>`
    margin-bottom: 4px;
    ${({ $isLive }) => {
        if ($isLive) {
            return css`
                display: none;
            `;
        }
    }}
`;

const StyledBidCount = styled(Caption)<{ $isSold: boolean }>`
    display: block;
    ${({ $isSold }) => {
        if ($isSold) {
            return css`
                display: none;
            `;
        }
    }}
`;

const LiveNowText = styled(BodySecondary)<{ $isLive: boolean }>`
    margin-bottom: 4px;
    display: none;
    ${({ $isLive }) => {
        if ($isLive) {
            return css`
                display: block;
            `;
        }
    }}
`;

const HeaderSpacer = styled.div`
    height: 67px;
`;

const NextFavoriteText = styled(BodySecondary)`
    margin-bottom: 4px;
`;
